/**
 * A tela de login do painel — specs 0085 e 0087.
 *
 * Na 0087 saíram o carrossel de módulos (seis cartões girando a cada 3 s, com
 * ícones decorativos e cores fora da marca), os ícones dentro dos campos e o
 * link "Esqueceu a senha?", que não levava a lugar nenhum: o painel não tem
 * recuperação de senha, e quem redefine é a administração, no cadastro de
 * usuários. A tela diz isso em vez de prometer um caminho que não existe.
 *
 * O campo de acesso aceita o e-mail ou o nome de usuário (`teste.admfin`), e
 * o rótulo passou a dizer as duas coisas.
 */

import { useState, FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { login } from '../services/auth';
import './LoginNew.css';

interface LoginProps {
  onLoginSuccess: () => void;
}

const LoginNew: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await login(email, senha, rememberMe);

      if (result.success) {
        onLoginSuccess();
      } else {
        setError(result.message || 'Credenciais inválidas');
      }
    } catch {
      setError('Não foi possível falar com o servidor. Confira a conexão e tente de novo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-new-container">
      <aside className="login-new-branding">
        <img src="/assets/logo-cooper-login.png" alt="Coopertransmig" className="login-new-brand-logo-img" />
        <p className="login-new-brand-tagline">
          Plataforma de gestão de transportes.
        </p>
        <p className="login-new-branding-footer">© 2026 Tresco</p>
      </aside>

      <main className="login-new-form-side">
        <div className="login-new-form-container">
          <div className="login-new-form-header">
            <div className="login-new-form-logo-wrapper">
              <img src="/assets/logo_coopertransmig.jpg" alt="Coopertransmig" className="login-new-form-logo-img" />
            </div>
            <h1>Entrar no painel</h1>
          </div>

          <form onSubmit={handleSubmit} className="login-new-form" aria-busy={loading}>
            <div className="login-new-input-group">
              <label htmlFor="email">E-mail ou usuário</label>
              <input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'login-erro' : undefined}
              />
            </div>

            <div className="login-new-input-group">
              <label htmlFor="senha">Senha</label>
              <div className="login-new-input-wrapper">
                <input
                  id="senha"
                  type={showPassword ? 'text' : 'password'}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                  required
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'login-erro' : undefined}
                />
                <button
                  type="button"
                  className="login-new-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar a senha' : 'Mostrar a senha'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                </button>
              </div>
            </div>

            <label className="login-new-checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Manter-me conectado
            </label>

            {error && (
              <p id="login-erro" className="login-new-error" role="alert">{error}</p>
            )}

            <button type="submit" className="login-new-submit-btn" disabled={loading}>
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <p className="login-new-ajuda">
            Esqueceu a senha? Peça uma nova a quem administra os cadastros na cooperativa.
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginNew;
